// web/src/preferences/useTheme.ts
//
// Reads localStorage['theme'] (one of 'light' | 'dark' | 'system'; default 'system'),
// resolves 'system' against prefers-color-scheme, toggles the `.light` class on <html>.
// Dark is the default (no class); `.light` is added only when the resolved theme is light.
// Listens for: themechange custom events (from ThemeSelect), matchMedia changes,
// and storage events (cross-tab sync).
import { useEffect, useState } from "react";

export type Theme = "light" | "dark" | "system";
export const THEME_STORAGE_KEY = "theme";
export const THEME_CHANGE_EVENT = "themechange";

function readPreference(): Theme {
  const value = localStorage.getItem(THEME_STORAGE_KEY);
  return value === "light" || value === "dark" || value === "system" ? value : "system";
}

function resolveSystem(): "light" | "dark" {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function resolve(pref: Theme): "light" | "dark" {
  return pref === "system" ? resolveSystem() : pref;
}

function apply(pref: Theme) {
  if (resolve(pref) === "light") {
    document.documentElement.classList.add("light");
  } else {
    document.documentElement.classList.remove("light");
  }
}

export function useTheme() {
  const [preference, setPreferenceState] = useState<Theme>(readPreference);

  useEffect(() => {
    apply(preference);
  }, [preference]);

  useEffect(() => {
    const onThemeChange = () => {
      const next = readPreference();
      setPreferenceState(next);
      apply(next);
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === THEME_STORAGE_KEY) {
        const next = readPreference();
        setPreferenceState(next);
        apply(next);
      }
    };
    window.addEventListener(THEME_CHANGE_EVENT, onThemeChange);
    window.addEventListener("storage", onStorage);

    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onSystemChange = () => {
      if (readPreference() === "system") apply("system");
    };
    mql.addEventListener("change", onSystemChange);

    return () => {
      window.removeEventListener(THEME_CHANGE_EVENT, onThemeChange);
      window.removeEventListener("storage", onStorage);
      mql.removeEventListener("change", onSystemChange);
    };
  }, []);

  function setPreference(pref: Theme) {
    setPreferenceState(pref);
    setThemePreference(pref);
  }

  return { preference, setPreference };
}

export function setThemePreference(pref: Theme) {
  localStorage.setItem(THEME_STORAGE_KEY, pref);
  window.dispatchEvent(new Event(THEME_CHANGE_EVENT));
}
